/*
 * -----------------------------------------------------------------------------
 *  Lucom Interaction Platform
 *  (C) Lucom GmbH, Erkrath. All rights reserved.
 * -----------------------------------------------------------------------------
 */

import type { Configuration } from "@/shared/types";
import { logLevels } from "../shared/data";

const nop = function () {};

const ROOT_LOGGER = "_ROOT";

export const LEVEL_DEBUG = "debug";
export const LEVEL_INFO = "info";
export const LEVEL_WARN = "warn";
export const LEVEL_ERROR = "error";
const LEVELS = [LEVEL_DEBUG, LEVEL_INFO, LEVEL_WARN, LEVEL_ERROR];
export type level = typeof LEVEL_DEBUG | typeof LEVEL_INFO | typeof LEVEL_WARN | typeof LEVEL_ERROR;

logLevels.set(ROOT_LOGGER, LEVEL_ERROR);

function assertValidLevel(level: level) {
    return LEVELS.includes(level);
}

class Logger {
    name: string;

    constructor(name: string) {
        if (typeof name === "undefined") throw new Error("missing name!");

        this.name = name;
    }

    get level() {
        let name = this.name;
        while (name) {
            const level = logLevels.get(name);
            if (level) return level;
            const pos = name.lastIndexOf(".");
            if (pos >= 0) name = name.substring(0, pos);
            else if (name !== ROOT_LOGGER) name = ROOT_LOGGER;
            else break;
        }

        return undefined;
    }

    get debugEnabled() {
        if (this.level) return [LEVEL_DEBUG].includes(this.level);
        else return false;
    }

    get infoEnabled() {
        if (this.level) return [LEVEL_DEBUG, LEVEL_INFO].includes(this.level);
        else return false;
    }

    get warnEnabled() {
        if (this.level) return [LEVEL_DEBUG, LEVEL_INFO, LEVEL_WARN].includes(this.level);
        else return false;
    }

    debug(message: string) {
        if (this.debugEnabled) ((console && console.log) || nop).apply(this, this.formatMessage(message));
        return this;
    }

    info(message: string) {
        if (this.infoEnabled) ((console && (console.info || console.log)) || nop).apply(this, this.formatMessage(message));
        return this;
    }

    // exception : any object that was thrown, optional
    warn(message: string, exception?: Error) {
        if (this.warnEnabled) ((console && (console.warn || console.log)) || nop).apply(this, this.formatMessage(message, exception));

        return this;
    }

    // exception : any object that was thrown, optional
    error(message: string, exception?: Error) {
        ((console && (console.error || console.log)) || nop).apply(this, this.formatMessage(message, exception));
        return this;
    }

    dir(object: any) {
        (console.dirxml || console.dir || nop).apply(this, object);
        return this;
    }

    indent(groupName: string) {
        if (console && console.group) console.group(groupName);
        return this;
    }

    outdent() {
        if (console && console.groupEnd) console.groupEnd();
        return this;
    }

    // exception: optional object that was caught with a catch (e) clause
    formatMessage(message: string, exception?: Error) {
        const args = [`[${this.name}] ${message}`];
        if (typeof exception !== "undefined") {
            if (exception.message) args.push(`\nerror.message: ${exception.message}`);
            else args.push(`\nerror: ${exception}`);
            if (exception.stack) args.push(`\nstack: ${exception.stack}`);
        }
        return args;
    }
}

export default Logger;

export function getLevel(name: string) {
    return logLevels.get(name);
}

export function setLevel(name: string, level: level) {
    if (!level) unsetLevel(name);
    else {
        level = level.toLowerCase() as level;
        assertValidLevel(level);
        logLevels.set(name, level);
    }
}

export function unsetLevel(name: string) {
    if (name !== ROOT_LOGGER) logLevels.delete(name);
}

export function reset(rootLevel: level) {
    logLevels.clear();
    setRootLevel(rootLevel || LEVEL_ERROR);
}

export function getRootLevel() {
    return getLevel(ROOT_LOGGER);
}

export function setRootLevel(level: level) {
    setLevel(ROOT_LOGGER, level);
}
//TODO: Typisierung von configutation
export function initialize(configuration: Configuration) {
    const rootLevel = configuration.logLevel ? configuration.logLevel : "warn";
    const customLevels = configuration.logLevels || {};

    reset(rootLevel);

    Object.keys(customLevels).forEach(function (name) {
        const level = customLevels[name];
        setLevel(name, level);
    });
}

export function buildLogConfigurationAccessor() {
    return {
        get: (name: string) => getLevel(name),
        set: (name: string, level: level) => setLevel(name, level),
        unset: (name: string) => unsetLevel(name),
        reset: (rootLevel: level) => reset(rootLevel), // rootLevel defaults to LEVEL_ERROR
        levels: logLevels, // direct access to the map
    };
}
